import React, { useEffect, useRef, useCallback } from "react";
import { Spine } from "pixi-spine";
import { Container } from "@pixi/react";
import { getAsset } from "../../assets";
import { useSelector, useDispatch } from "react-redux";
import { playSound } from "../../State/Slices/soundsSlice";
import { SOUND_NAME } from "../../sounds";
import {
  setFreeSpinIntroAnimationCompleted,
  setFreeSpinIntroAnimationStarted,
  setShowFreeSpinIntro,
} from "../../State/Slices/animationsSlice";
import { stopSound } from "../../State/Slices/soundsSlice";
import { Assets } from "@pixi/assets";

const FreeSpinIntro = ({ animationPath, x, y, zIndex = 0, symbolSize }) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const spineAnimRef = useRef(null);
  const isAnimationLoaded = useRef(false);
  const isAnimationPlaying = useRef(false);

  const { freeSpinIntroAnimationCompleted, triggerFreeSpinIntroOutPhase } =
    useSelector((state) => state.animations);
  const { status } = useSelector((state) => state.assets);
  const { backgroundMusicEnabled } = useSelector((state) => state.sound);
  const playAnimationSequence = useCallback(
    (spineAnim, animations, index = 0) => {
      if (
        !spineAnim ||
        index >= animations.length ||
        freeSpinIntroAnimationCompleted
      ) {
        return;
      }

      const { name } = animations[index];
      const isLoop = name.includes("Loop");
      const isInPhase = name.includes("In");
      const isOutPhase = name.includes("Out");

      if (isInPhase) {
        spineAnim.state.setAnimation(0, name, false);
        spineAnim.state.clearListeners();
        spineAnim.state.addListener({
          complete: () => {
            playAnimationSequence(spineAnim, animations, index + 1);
          },
        });
      } else if (isLoop) {
        if (!freeSpinIntroAnimationCompleted) {
          spineAnim.state.setAnimation(0, name, true);
          isAnimationPlaying.current = true;
          console.log(`Playing loop animation: ${name}`);
        }
      } else if (isOutPhase && triggerFreeSpinIntroOutPhase) {
        spineAnim.state.setAnimation(0, name, false);
        spineAnim.state.clearListeners();
        spineAnim.state.addListener({
          complete: () => {
            dispatch(stopSound(SOUND_NAME.freeSpinIntro));

            if (backgroundMusicEnabled) {
              dispatch(playSound(SOUND_NAME.freeSpinMusic));
            }

            dispatch(setFreeSpinIntroAnimationStarted(false));
            dispatch(setFreeSpinIntroAnimationCompleted(true));
            dispatch(setShowFreeSpinIntro(false));
            isAnimationPlaying.current = false;
          },
        });
      }
    },
    [
      dispatch,
      freeSpinIntroAnimationCompleted,
      triggerFreeSpinIntroOutPhase,
      backgroundMusicEnabled,
    ]
  );

  const playStageAnimation = useCallback(
    (spineAnim) => {
      if (!spineAnim || !spineAnim.spineData) {
        console.warn("spineAnim is not initialized or spineData is missing.");
        return;
      }

      const animations = spineAnim.spineData.animations.map((anim) => ({
        name: anim.name,
        duration: anim.duration,
      }));
      playAnimationSequence(spineAnim, animations);
    },
    [playAnimationSequence]
  );

  useEffect(() => {
    if (freeSpinIntroAnimationCompleted) return;

    const loadSpineAnimation = async () => {
      try {
        let spineResource = getAsset(status, animationPath);
        if (!spineResource?.spineData) {
          console.warn(
            "Spine resource or data is missing! Attempting to load asset..."
          );
          await Assets.load(animationPath);
          const newSpineResource = getAsset(status, animationPath);
          if (!newSpineResource?.spineData) {
            console.error(
              "Failed to load spine resource after attempting to load it."
            );
            return;
          }
          spineResource = newSpineResource;
        }

        const spineAnim = new Spine(spineResource.spineData);
        spineAnimRef.current = spineAnim;
        isAnimationLoaded.current = true;

        spineAnim.position.set(0, 0);
        spineAnim.width = symbolSize * 7;
        spineAnim.height = symbolSize * 7;

        if (containerRef.current) {
          containerRef.current.addChild(spineAnim);
          playStageAnimation(spineAnim);
        }
      } catch (error) {
        console.error("Error loading Spine animation:", error);
      }
    };

    loadSpineAnimation();
    dispatch(playSound(SOUND_NAME.freeSpinIntro));
    dispatch(stopSound(SOUND_NAME.backgroundMusic));
    dispatch(setFreeSpinIntroAnimationStarted(true));

    return () => {
      console.log("Cleaning up FreeSpinIntro component...");

      if (spineAnimRef.current) {
        const spineAnim = spineAnimRef.current;
        if (spineAnim.state) {
          spineAnim.state.clearListeners();
        }
      } else {
        console.warn("Skipping cleanup: Animation is still playing.");
      }
    };
  }, [animationPath, dispatch, status, freeSpinIntroAnimationCompleted]);

  useEffect(() => {
    if (
      triggerFreeSpinIntroOutPhase &&
      spineAnimRef.current &&
      isAnimationLoaded.current &&
      !freeSpinIntroAnimationCompleted
    ) {
      const spineAnim = spineAnimRef.current;
      const animations = spineAnim.spineData.animations.map((anim) => ({
        name: anim.name,
        duration: anim.duration,
      }));
      const outPhaseIndex = animations.findIndex((anim) =>
        anim.name.includes("Out")
      );
      if (outPhaseIndex !== -1) {
        playAnimationSequence(spineAnim, animations, outPhaseIndex);
      }
    }
  }, [
    triggerFreeSpinIntroOutPhase,
    freeSpinIntroAnimationCompleted,
    playAnimationSequence,
  ]);

  useEffect(() => {
    if (spineAnimRef.current) {
      const spineAnim = spineAnimRef.current;

      spineAnim.width = symbolSize * 7;
      spineAnim.height = symbolSize * 7;
    }
  }, [symbolSize]);

  return (
    <Container
      ref={containerRef}
      x={x}
      y={y}
      zIndex={zIndex}
      name="Spine Win Animation"
      sortChildren={true}
    />
  );
};

export default FreeSpinIntro;
